import { useState } from 'react';
import type React from 'react';
import { ArrowRight, FilePlus2, RefreshCw } from 'lucide-react';
import type { FolgeAuswahlZeile } from '../../services/folgeunterlagen/folgeunterlagen';
import type { ErzeugungsErgebnis, FolgeArt } from '../../services/folgeunterlagen/typen';
import {
    FOLGE_FUSS_SATZ,
    FOLGE_NOCH_NICHT_SATZ,
    FOLGE_UNGESPEICHERT_SATZ,
    ergebnisAnzeige,
    gewaehlteArten,
    istGewaehlt,
    type FolgeHaken,
} from './folgeunterlagenAnzeige';

/**
 * Karte „Folgeunterlagen" (Station 9 und Reiter „Übergabe").
 *
 * Je Unterlage Titel, Pflicht, Bestand und — wenn waehlbar — ein Haken;
 * darunter „Entwürfe anlegen" (ohne Abschluss) und die Ergebnisliste der
 * letzten Anlage. Reine Darstellung: Lesen und Anlegen liegen im Container
 * (`useFolgeunterlagen`), die Regeln in `folgeunterlagenAnzeige.ts`. Eigener
 * Zustand sind nur die angeklickten Haken.
 */
export const FolgeunterlagenKarte: React.FC<{
    zeilen: FolgeAuswahlZeile[];
    laden: boolean;
    lesefehler: string | null;
    ergebnisse: ErzeugungsErgebnis[] | null;
    /** Planung erfuellt oder abgeschlossen (`folgeunterlagenMoeglich`). */
    moeglich: boolean;
    isReadOnly: boolean;
    /** Eine andere Aktion des Moduls laeuft. */
    laeuft: boolean;
    anlageLaeuft: boolean;
    /** Ungespeicherte Grundlagen — sie fliessen nicht ein. */
    schmutzig: boolean;
    onNeuLesen: () => void;
    onAnlegen: (arten: FolgeArt[]) => void;
    onZumModul: (art: FolgeArt) => void;
}> = ({
    zeilen, laden, lesefehler, ergebnisse, moeglich, isReadOnly, laeuft, anlageLaeuft, schmutzig,
    onNeuLesen, onAnlegen, onZumModul,
}) => {
    const [haken, setHaken] = useState<FolgeHaken>({});
    const mitHaken = moeglich && !isReadOnly;
    const gewaehlt = gewaehlteArten(zeilen, haken);
    const titelVon = (art: FolgeArt): string => zeilen.find(z => z.art === art)?.titel || art;

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start gap-3">
                <FilePlus2 className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-800 text-sm mb-1">Folgeunterlagen</h3>
                    <p className="text-xs text-slate-600 leading-snug">
                        Aus den gespeicherten Angaben der Planung legt die App SiGe-Plan, Handbuch,
                        Baustellenordnung und A+S-Plan als Entwurf an.
                    </p>
                </div>
                {moeglich && (
                    laden ? (
                        <span className="shrink-0 text-[11px] text-slate-400">wird geprüft …</span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onNeuLesen()}
                            disabled={anlageLaeuft}
                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-700 disabled:opacity-40"
                        >
                            <RefreshCw className="w-3 h-3" /> Bestand neu prüfen
                        </button>
                    )
                )}
            </div>

            {!moeglich && <p className="mt-3 text-xs text-slate-600 leading-snug">{FOLGE_NOCH_NICHT_SATZ}</p>}
            {lesefehler && <p className="mt-3 text-xs text-slate-700 leading-snug">{lesefehler}</p>}

            <ul className="mt-3 divide-y divide-slate-100">
                {zeilen.map(z => (
                    <li key={z.art} className="py-2 flex items-start gap-3 min-w-0">
                        {mitHaken && (
                            <span className="w-4 shrink-0 pt-0.5">
                                {z.waehlbar && (
                                    <input
                                        type="checkbox"
                                        checked={istGewaehlt(z, haken)}
                                        onChange={e => {
                                            const an = e.target.checked;
                                            setHaken(h => ({ ...h, [z.art]: an }));
                                        }}
                                        disabled={anlageLaeuft || laeuft}
                                        aria-label={`${z.titel} als Entwurf anlegen`}
                                        className="w-3.5 h-3.5 accent-slate-700"
                                    />
                                )}
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-slate-800">{z.titel}</div>
                            <div className="text-[11px] text-slate-500" title={z.begruendung || undefined}>{z.grund}</div>
                        </div>
                        {(z.bestand || laden) && (
                            <span className="shrink-0 max-w-[50%] text-right text-[11px] text-slate-600">{z.bestandText}</span>
                        )}
                    </li>
                ))}
            </ul>

            {mitHaken && (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={() => onAnlegen(gewaehlt)}
                        disabled={laeuft || anlageLaeuft || gewaehlt.length === 0}
                        className="px-3 py-2 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                    >
                        Entwürfe anlegen
                    </button>
                    {anlageLaeuft && <span className="text-[11px] text-slate-500">Entwürfe werden angelegt …</span>}
                    {schmutzig && <span className="text-[11px] text-slate-500 flex-1 min-w-[12rem]">{FOLGE_UNGESPEICHERT_SATZ}</span>}
                </div>
            )}
            {ergebnisse && ergebnisse.length > 0 && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Ergebnis der Anlage</div>
                    <ul className="divide-y divide-slate-100">
                        {ergebnisse.map(e => {
                            const z = ergebnisAnzeige(e, titelVon(e.art));
                            return (
                                <li key={z.art} className="py-2 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-bold text-slate-800">{z.titel}</span>
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                                            z.fehler ? 'border-rose-300 text-rose-700' : 'border-slate-300 text-slate-600'
                                        }`}>
                                            {z.marke}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => onZumModul(z.art)}
                                            disabled={anlageLaeuft}
                                            className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                                        >
                                            Zum Modul <ArrowRight className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {z.satz && <p className="mt-0.5 text-xs text-slate-600 leading-snug break-words">{z.satz}</p>}
                                    {z.uebernommenText && (
                                        <details className="mt-1">
                                            <summary className="cursor-pointer text-[11px] font-bold text-slate-500">{z.uebernommenText}</summary>
                                            <ul className="mt-1 ml-4 list-disc text-[11px] text-slate-600 space-y-0.5">
                                                {z.uebernommen.map((s, i) => <li key={i}>{s}</li>)}
                                            </ul>
                                        </details>
                                    )}
                                    {z.offen.length > 0 && (
                                        <div className="mt-1">
                                            <div className="text-[11px] font-bold text-slate-700">Noch zu tun:</div>
                                            <ul className="ml-4 list-disc text-[11px] text-slate-600 space-y-0.5">
                                                {z.offen.map((s, i) => <li key={i}>{s}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <p className="mt-4 text-[11px] text-slate-400 leading-snug">{FOLGE_FUSS_SATZ}</p>
        </section>
    );
};
